import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Dropdown,
} from 'reactstrap';

import Page from './Page';
import {
  faClipboardQuestion,
  faListTree,
} from '@fortawesome/pro-regular-svg-icons';
import { Icon } from 'js/components';
import { computeAnchoredOverlayVertical } from 'js/utils/anchored-overlay';
import { useEditorContext } from 'js/ydoc/docs/capabilities-assessment/EditorContext';
import { autoScrollWindowForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { STANDARD_QUESTION_DRAG_GROUP } from './Page/StandardQuestionsPage/StandardQuestionsPage';

import styles from './CapabilitiesAssessmentEditor.module.scss';
// import { convertToAssessment } from 'js/ydoc/docs/capabilities-assessment/convert';

const PAGE_TYPES = [
  {
    type: 'capability-questions',
    name: 'Capability questions',
    icon: faListTree,
  },
  {
    type: 'standard-questions',
    name: 'Standard questions',
    icon: faClipboardQuestion,
  },
];

// Space between the button and the menu (see `.add-page-menu`)
const ADD_PAGE_MENU_GAP = 4;

function Editor() {
  const mainPaneRef = useRef(null);
  const {
    assessment,
    addPage,
    moveStandardElementBefore,
    moveStandardElementAfter,
    moveStandardElementToPage,
  } = useEditorContext();

  useEffect(() => {
    return autoScrollWindowForElements();
  }, []);

  // Standard questions move within and between standard pages
  useEffect(() => {
    return monitorForElements({
      canMonitor: ({ source }) =>
        source.data.dragGroup === STANDARD_QUESTION_DRAG_GROUP,
      onDrop({ source, location }) {
        const target = location.current.dropTargets[0];
        if (!target) {
          return;
        }

        const questionGuid = source.data.question.guid;

        if (target.data.question) {
          if (extractClosestEdge(target.data) === 'top') {
            moveStandardElementBefore(questionGuid, target.data.question.guid);
          } else {
            moveStandardElementAfter(questionGuid, target.data.question.guid);
          }
        } else if (target.data.page) {
          moveStandardElementToPage(questionGuid, target.data.page.guid);
        }
      },
    });
  }, [
    moveStandardElementBefore,
    moveStandardElementAfter,
    moveStandardElementToPage,
  ]);

  const addPageMenuRef = useRef(null);
  const [isAddPageMenuOpen, setIsAddPageMenuOpen] = useState(false);
  const [addPageMenuDirection, setAddPageMenuDirection] = useState('down');

  const toggleAddPageMenu = useCallback(() => {
    setIsAddPageMenuOpen((isOpen) => !isOpen);
  }, []);

  // Opens below the button unless the menu doesn't fit there. The theme pins
  // dropdown menus below their toggle, so only the dropup class flips one;
  // measuring the rendered menu here, before paint, means it never shows in
  // the wrong place.
  useLayoutEffect(() => {
    if (!isAddPageMenuOpen) {
      setAddPageMenuDirection('down');
      return;
    }

    const wrapper = addPageMenuRef.current;
    const menu = wrapper?.querySelector('.dropdown-menu');
    if (!wrapper || !menu) {
      return;
    }

    const placement = computeAnchoredOverlayVertical({
      triggerRect: wrapper.getBoundingClientRect(),
      overlayHeight: menu.getBoundingClientRect().height,
      viewportHeight: window.innerHeight,
      gap: ADD_PAGE_MENU_GAP,
    });

    setAddPageMenuDirection(placement === 'above' ? 'up' : 'down');
  }, [isAddPageMenuOpen]);

  const handleAddPage = useCallback(
    (pageType) => {
      addPage(pageType);
    },
    [addPage]
  );

  return (
    <div ref={mainPaneRef}>
      {assessment &&
        assessment.pages.map((page) => <Page key={page.guid} page={page} />)}
      <div ref={addPageMenuRef}>
        <Dropdown
          isOpen={isAddPageMenuOpen}
          toggle={toggleAddPageMenu}
          direction={addPageMenuDirection}
          className={styles['add-page-menu']}
        >
          <DropdownToggle tag="button" className={styles['add-button']}>
            <i>
              <Icon icon="plus" className="btn-icon-left" />
            </i>
            Add a page
          </DropdownToggle>
          <DropdownMenu>
            {PAGE_TYPES.map(({ type, name, icon }) => (
              <DropdownItem key={type} onClick={() => handleAddPage(type)}>
                <Icon icon={icon} className="btn-icon-left" fixedWidth />
                {name}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
      </div>
    </div>
  );
}

export default Editor;
